import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Typography,
  FormControlLabel, Switch,
} from '@mui/material';
import type { Account } from '../../types/api';
import type { BillPayee } from '../../services/bills-service';
import { AccountSelect } from '../AccountSelect';

const CATEGORIES = ['CreditCard', 'Housing', 'Utility', 'Telecom', 'Tax',
  'Insurance', 'Vehicle', 'Other'];

/** Accounts money can leave from: cash, and cards for bills paid via Chexy. */
export const FUNDING_TYPES: Account['account_type'][] = ['Asset', 'Debt'];
/** What a payee's balance is owed on: a card, a loan, a mortgage. */
const LIABILITY_TYPES: Account['account_type'][] = ['Debt'];

/** Initial values when adding, e.g. from a detected recurring payment. */
export interface PayeeDraft {
  name?: string;
  category?: string;
  typical_due_day?: number;
  funding_account_id?: string | null;
}

interface Props {
  open: boolean;
  /** The payee being edited, or null to add one. */
  editing: BillPayee | null;
  draft?: PayeeDraft;
  accounts: Account[];
  busy: boolean;
  onClose: () => void;
  /** Receives the FULL payee: saving is an upsert that replaces every field. */
  onSave: (p: Partial<BillPayee> & { name: string }) => void;
}

/**
 * Add or edit a bill payee.
 *
 * Edit sends back every field it did not show (payment method, owner,
 * currency, notes): the server's save is an upsert that overwrites them all,
 * so leaving one out would quietly reset it. The name is fixed while editing
 * because the upsert is keyed on it -- a rename would create a second payee.
 */
export const PayeeDialog: React.FC<Props> = ({
  open, editing, draft, accounts, busy, onClose, onSave,
}) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Other');
  const [day, setDay] = useState('1');
  const [funding, setFunding] = useState('');
  const [liability, setLiability] = useState('');
  const [scheduled, setScheduled] = useState(false);
  const [daysBefore, setDaysBefore] = useState('');

  useEffect(() => {
    if (!open) return;
    const src = editing ?? draft ?? {};
    setName(src.name ?? '');
    setCategory(src.category ?? 'Other');
    setDay(String(src.typical_due_day ?? 1));
    setFunding(src.funding_account_id ?? '');
    setLiability(editing?.liability_account_id ?? '');
    setScheduled(editing?.schedule_enabled ?? false);
    setDaysBefore(editing?.pay_days_before_due != null ? String(editing.pay_days_before_due) : '');
  }, [open, editing, draft]);

  // The server refuses an enabled schedule without a 0-28 offset; say so here
  // instead of after a round trip.
  const days = daysBefore.trim() === '' ? null : Number(daysBefore);
  const daysInvalid = days !== null && (!Number.isInteger(days) || days < 0 || days > 28);
  const scheduleInvalid = scheduled && (days === null || daysInvalid);

  const submit = () => onSave({
    ...(editing ?? {}),
    name: name.trim(),
    category: category as BillPayee['category'],
    typical_due_day: Number(day) || undefined,
    funding_account_id: funding || null,
    liability_account_id: liability || null,
    is_active: editing?.is_active ?? true,
    // Sent every time, on or off: an omitted schedule_enabled would keep the
    // stored rule, so switching it off must be explicit.
    schedule_enabled: scheduled,
    pay_days_before_due: daysInvalid ? null : days,
  });

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add a payee'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A payee is the thing you pay each period — a card, a utility, a
          lease. Bills are then tracked against it month by month.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Name" size="small" fullWidth autoFocus={!editing} value={name}
            onChange={(e) => setName(e.target.value)} disabled={!!editing}
          />
          <TextField
            label="Category" select size="small" fullWidth value={category}
            onChange={(e) => setCategory(e.target.value)}
            helperText="Credit cards revolve, so they are tracked for paid-in-full"
          >
            {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField
            label="Typical due day" size="small" fullWidth value={day}
            onChange={(e) => setDay(e.target.value)}
            helperText="Day of the month it is usually due"
          />
          <AccountSelect
            label="Paid from" accounts={accounts} types={FUNDING_TYPES}
            value={funding} onChange={setFunding} allowNone
            helperText="Default account the money leaves"
          />
          <AccountSelect
            label="Liability account" accounts={accounts} types={LIABILITY_TYPES}
            value={liability} onChange={setLiability} allowNone
            helperText="The card or loan this payee's balance is owed on. Used to match payments and read statements."
          />
          <FormControlLabel
            control={<Switch checked={scheduled} onChange={(e) => setScheduled(e.target.checked)} />}
            label="Scheduled payment"
          />
          {scheduled && (
            <TextField
              label="Days before due" size="small" fullWidth type="number" value={daysBefore}
              onChange={(e) => setDaysBefore(e.target.value)}
              inputProps={{ min: 0, max: 28, step: 1 }}
              error={daysInvalid}
              helperText={daysInvalid
                ? 'Enter 0 to 28 days'
                : 'e.g. 4 → paid on the 12th when due the 16th. Marked paid automatically once the bank shows the exact amount.'}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !name.trim() || scheduleInvalid}>
          {busy ? 'Saving…' : editing ? 'Save payee' : 'Add payee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
